// BUILD-042 (Oct 24) - what the normal employee page needs to know about guided setup: is it unfinished (-> Continue setup), finished (-> badge), or absent
// (a legacy record, grandfathered)? Read from the SERVER (GET .../setup), never guessed. A failed read is its own state and is never treated as "finished",
// "not started" or "legacy".
import { useCallback, useEffect, useRef, useState } from 'react';
import { getHrSetup, type HrSetupView } from '../../../api/client';

export interface SetupGate {
  state: 'loading' | 'ready' | 'error';
  view: HrSetupView | null;
  error: string | null;
  reload: () => Promise<void>;
}

export function useSetupGate(employeeId: string | undefined): SetupGate {
  const [state, setState] = useState<SetupGate['state']>('loading');
  const [view, setView] = useState<HrSetupView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(employeeId);
  current.current = employeeId;

  const reload = useCallback(async () => {
    if (!employeeId) return;
    try {
      const v = await getHrSetup(employeeId);
      if (current.current !== employeeId) return;
      setView(v); setError(null); setState('ready');
    } catch (e: any) {
      if (current.current !== employeeId) return;
      // A failed refresh keeps the last good view; only a first failure is an error state.
      setState((s) => (s === 'ready' ? 'ready' : 'error'));
      setError(e?.status === 403 || e?.status === 404 ? 'HR setup status isn’t available to your account for this person.' : (e?.message || 'HR setup status could not be loaded.'));
    }
  }, [employeeId]);

  useEffect(() => {
    setState('loading'); setView(null); setError(null);
    void reload();
  }, [employeeId, reload]);

  return { state, view, error, reload };
}
